/**
 * Coordinate helpers for PDF.js pagesRotation.
 *
 * Custom overlays (highlights, underlines, notes, textboxes, drawings) are
 * stored in scale-1 viewer coordinates at pagesRotation = 0. When the viewer
 * rotates, PDF.js re-renders the canvas/text layer for the new orientation,
 * but our overlays must be transformed into that same display space.
 */

export const normalizeRotation = (rotation) =>
  (((Number(rotation) || 0) % 360) + 360) % 360;

/** Unrotated (base) size given current display size at the same scale. */
export const getBasePageSizeFromDisplay = (
  displayWidth,
  displayHeight,
  rotation,
) => {
  const r = normalizeRotation(rotation);
  if (r === 90 || r === 270) {
    return {
      width: Number(displayHeight) || 0,
      height: Number(displayWidth) || 0,
    };
  }
  return {
    width: Number(displayWidth) || 0,
    height: Number(displayHeight) || 0,
  };
};

/** Display size given unrotated (base) size at the same scale. */
export const getDisplayPageSize = (baseWidth, baseHeight, rotation) => {
  const r = normalizeRotation(rotation);
  if (r === 90 || r === 270) {
    return { width: Number(baseHeight) || 0, height: Number(baseWidth) || 0 };
  }
  return { width: Number(baseWidth) || 0, height: Number(baseHeight) || 0 };
};

/**
 * Rotate a point from base (pagesRotation=0) scale-1 space into the current
 * rotated scale-1 display space. Rotation is clockwise to match PDF.js.
 */
export const rotatePoint = (x, y, baseWidth, baseHeight, rotation) => {
  const r = normalizeRotation(rotation);
  const px = Number(x) || 0;
  const py = Number(y) || 0;
  const w = Number(baseWidth) || 0;
  const h = Number(baseHeight) || 0;

  switch (r) {
    case 90:
      return { x: h - py, y: px };
    case 180:
      return { x: w - px, y: h - py };
    case 270:
      return { x: py, y: w - px };
    default:
      return { x: px, y: py };
  }
};

/** Inverse of rotatePoint: rotated scale-1 display → base scale-1. */
export const unrotatePoint = (x, y, baseWidth, baseHeight, rotation) => {
  const r = normalizeRotation(rotation);
  const px = Number(x) || 0;
  const py = Number(y) || 0;
  const w = Number(baseWidth) || 0;
  const h = Number(baseHeight) || 0;

  switch (r) {
    case 90:
      return { x: py, y: h - px };
    case 180:
      return { x: w - px, y: h - py };
    case 270:
      return { x: w - py, y: px };
    default:
      return { x: px, y: py };
  }
};

export const rotateRect = (rect, baseWidth, baseHeight, rotation) => {
  const left = Number(rect?.left || 0);
  const top = Number(rect?.top || 0);
  const width = Number(rect?.width || 0);
  const height = Number(rect?.height || 0);
  const r = normalizeRotation(rotation);
  if (r === 0) {
    return { left, top, width, height };
  }

  const corners = [
    rotatePoint(left, top, baseWidth, baseHeight, r),
    rotatePoint(left + width, top, baseWidth, baseHeight, r),
    rotatePoint(left, top + height, baseWidth, baseHeight, r),
    rotatePoint(left + width, top + height, baseWidth, baseHeight, r),
  ];
  const xs = corners.map((c) => c.x);
  const ys = corners.map((c) => c.y);
  const minX = Math.min(...xs);
  const minY = Math.min(...ys);
  return {
    left: minX,
    top: minY,
    width: Math.max(...xs) - minX,
    height: Math.max(...ys) - minY,
  };
};

export const unrotateRect = (rect, baseWidth, baseHeight, rotation) => {
  const left = Number(rect?.left || 0);
  const top = Number(rect?.top || 0);
  const width = Number(rect?.width || 0);
  const height = Number(rect?.height || 0);
  const r = normalizeRotation(rotation);
  if (r === 0) {
    return { left, top, width, height };
  }

  const corners = [
    unrotatePoint(left, top, baseWidth, baseHeight, r),
    unrotatePoint(left + width, top, baseWidth, baseHeight, r),
    unrotatePoint(left, top + height, baseWidth, baseHeight, r),
    unrotatePoint(left + width, top + height, baseWidth, baseHeight, r),
  ];
  const xs = corners.map((c) => c.x);
  const ys = corners.map((c) => c.y);
  const minX = Math.min(...xs);
  const minY = Math.min(...ys);
  return {
    left: minX,
    top: minY,
    width: Math.max(...xs) - minX,
    height: Math.max(...ys) - minY,
  };
};

/** Rotate a 0–1 normalized point (fraction of page width/height). */
export const rotateNormalizedPoint = (x, y, rotation) => {
  const r = normalizeRotation(rotation);
  const px = Number(x) || 0;
  const py = Number(y) || 0;

  switch (r) {
    case 90:
      return { x: 1 - py, y: px };
    case 180:
      return { x: 1 - px, y: 1 - py };
    case 270:
      return { x: py, y: 1 - px };
    default:
      return { x: px, y: py };
  }
};

export const unrotateNormalizedPoint = (x, y, rotation) => {
  const r = normalizeRotation(rotation);
  const px = Number(x) || 0;
  const py = Number(y) || 0;

  switch (r) {
    case 90:
      return { x: py, y: 1 - px };
    case 180:
      return { x: 1 - px, y: 1 - py };
    case 270:
      return { x: 1 - py, y: px };
    default:
      return { x: px, y: py };
  }
};

export const rotateNormalizedRect = (x, y, width, height, rotation) => {
  const r = normalizeRotation(rotation);
  const nx = Number(x) || 0;
  const ny = Number(y) || 0;
  const nw = Number(width) || 0;
  const nh = Number(height) || 0;
  if (r === 0) {
    return { x: nx, y: ny, width: nw, height: nh };
  }

  const corners = [
    rotateNormalizedPoint(nx, ny, r),
    rotateNormalizedPoint(nx + nw, ny, r),
    rotateNormalizedPoint(nx, ny + nh, r),
    rotateNormalizedPoint(nx + nw, ny + nh, r),
  ];
  const xs = corners.map((c) => c.x);
  const ys = corners.map((c) => c.y);
  const minX = Math.min(...xs);
  const minY = Math.min(...ys);
  return {
    x: minX,
    y: minY,
    width: Math.max(...xs) - minX,
    height: Math.max(...ys) - minY,
  };
};

export const unrotateNormalizedRect = (x, y, width, height, rotation) => {
  const r = normalizeRotation(rotation);
  const nx = Number(x) || 0;
  const ny = Number(y) || 0;
  const nw = Number(width) || 0;
  const nh = Number(height) || 0;
  if (r === 0) {
    return { x: nx, y: ny, width: nw, height: nh };
  }

  const corners = [
    unrotateNormalizedPoint(nx, ny, r),
    unrotateNormalizedPoint(nx + nw, ny, r),
    unrotateNormalizedPoint(nx, ny + nh, r),
    unrotateNormalizedPoint(nx + nw, ny + nh, r),
  ];
  const xs = corners.map((c) => c.x);
  const ys = corners.map((c) => c.y);
  const minX = Math.min(...xs);
  const minY = Math.min(...ys);
  return {
    x: minX,
    y: minY,
    width: Math.max(...xs) - minX,
    height: Math.max(...ys) - minY,
  };
};

/**
 * Resolve unrotated scale-1 page size from a rendered page element.
 * Display CSS size / scale, then undo the 90/270 swap if rotated.
 *
 * Prefer getBasePageSize() / getBasePageSizeFromPdfPage() when possible —
 * inferring from the canvas is racy while PDF.js is mid-rerender after a
 * pagesRotation change (canvas may still have the old orientation).
 */
export const getBasePageSizeFromPageEl = (pageEl, scale, rotation) => {
  const safeScale = Number(scale) > 0 ? Number(scale) : 1;
  const canvas = pageEl?.querySelector?.("canvas");
  const displayW =
    (canvas?.clientWidth || pageEl?.clientWidth || 0) / safeScale;
  const displayH =
    (canvas?.clientHeight || pageEl?.clientHeight || 0) / safeScale;
  return getBasePageSizeFromDisplay(displayW, displayH, rotation);
};

/** PDF.js CSS pixel scale (96 DPI / 72 PDF points). Matches viewer scale=1. */
const PDF_CSS_UNITS = 96 / 72;

/**
 * Unrotated (pagesRotation = 0) scale-1 page size from the PDF page itself.
 * Independent of the current canvas DOM size / live rotation.
 */
export const getBasePageSizeFromPdfPage = (pdfPage) => {
  if (!pdfPage?.getViewport) return null;
  try {
    const baseViewport = pdfPage.getViewport({ scale: PDF_CSS_UNITS });
    const width = Number(baseViewport?.width) || 0;
    const height = Number(baseViewport?.height) || 0;
    if (width <= 0 || height <= 0) return null;
    return { width, height };
  } catch {
    return null;
  }
};

/**
 * Best-effort unrotated scale-1 page size for overlay math.
 * Uses pdfPage viewport first; falls back to DOM inference.
 */
export const getBasePageSize = ({
  pdfViewer,
  pageNumber,
  pageEl,
  scale,
  rotation,
} = {}) => {
  const pageView = pdfViewer?.getPageView?.(Number(pageNumber) - 1);
  const fromPage = getBasePageSizeFromPdfPage(pageView?.pdfPage);
  if (fromPage) return fromPage;
  return getBasePageSizeFromPageEl(pageEl, scale, rotation);
};
